import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiTextfield,
  TuiAlertService,
  TuiLoader,
} from "@taiga-ui/core";
import {
  TuiTabs,
  TuiFiles,
  TuiDataListWrapper,
  TuiStringifyContentPipe,
} from "@taiga-ui/kit";
import { TuiNavigation } from "@taiga-ui/layout";
import {
  TuiInputModule,
  TuiTextfieldControllerModule,
  TuiTextareaModule,
  TuiInputPhoneModule,
  TuiComboBoxModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiInputDateModule,
  TuiInputDateTimeModule,
  TuiInputTimeModule,
} from "@taiga-ui/legacy";
import { ManualRecordService } from "../../../core/services/manualRecords.service";
import { Breadcrumb } from "../../../model/interface/breadcrumb";
import { TuiDay } from "@taiga-ui/cdk";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";
import { RecommendedHintComponent } from "../../../shared/components/recommended-hint/recommended-hint.component";

@Component({
  selector: "app-add-manual-record",
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    FormsModule,
    CommonModule,
    TuiAppearance,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiLoader,
    ReactiveFormsModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    TuiStringifyContentPipe,
    TuiInputDateModule,
    TuiTable,
    TuiInputDateTimeModule,
    RecommendedHintComponent,
    TuiInputTimeModule,
  ],
  templateUrl: "./add-manual-record.component.html",
  styleUrl: "./add-manual-record.component.scss",
})
export class AddManualRecordComponent implements OnInit {
  manualRecordForm!: FormGroup;
  isEditMode = false;
  record: any;
  recordId: any;
  isLoading = false;

  // Predefined procedures list
  predefinedProcedures = [
    'Catheter Inserting',
    'Wound Dressing',
    'IV Injection',
    'Ambulance Service',
    'Physiotherapy',
    'Nursing Care',
    'Doctor Care'
  ];

  // All available procedures (predefined + manually added)
  allProcedures: string[] = [...this.predefinedProcedures];
  
  // Dropdown options for procedures
  procedureOptions: string[] = [...this.predefinedProcedures, 'Other'];
  selectedProcedure = '';
  showAddNewProcedure = false;
  
  // New procedure input
  newProcedure = '';

  // Custom payment types
  customPayments: { name: string; value: number | null }[] = [];
  newPaymentName = '';
  newPaymentValue: number | null = null;

  // Multi-select configuration
  stringify = (item: string) => item;

  breadcrumbs: Breadcrumb[] = [
    { label: "Royal Hospital" },
    { label: "Manual Records", link: "admin/manual-records" },
    { label: "Add New Record" },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private alerts: TuiAlertService,
    private manualRecordService: ManualRecordService
  ) {
    this.manualRecordForm = this.fb.group({
      //disable cahrgeRefNo field
      chargeRefNo: [{ value: null, disabled: true }],
      nurseName: [null, Validators.required],
      clientName: [null, Validators.required],
      clientAddress: [null],
      clientContact: [null, Validators.required],
      doctorName: [null],
      procedures: [[], Validators.required],
      payments: this.fb.group({
        doctorPayment: [null],
        nursingPayment: [null],
        pharmacyPayment: [null],
        laboratoryPayment: [null],
        ambulancePayment: [null],
        physiotherapyPayment: [null],
        otherPayment: [null],
      }),
      grandTotal: [0],
      date: [new Date(), Validators.required],
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.recordId = params.get("id");
      if (this.recordId) {
        this.isEditMode = true;
        this.loadManualRecordData();
        this.breadcrumbs = [
          { label: "Royal Hospital" },
          { label: "Manual Records", link: "admin/manual-records" },
          { label: "Edit Record" },
        ];
      } else {
        const newDate = new Date();
        const FormattedDate = TuiDay.fromLocalNativeDate(new Date(newDate));
        this.manualRecordForm.patchValue({
          date: FormattedDate,
        });
      }
    });
    this.setupConditionalValidation();
    this.setupPaymentCalculation();
  }

  loadManualRecordData(): void {
    this.isLoading = true;
    this.manualRecordService.getManualRecordById(this.recordId).subscribe({
      next: (res: any) => {
        const FormattedDate = res.date
          ? TuiDay.fromLocalNativeDate(new Date(res.date))
          : null;
        this.record = res;
        
        // Handle procedure data - backend sends as array in 'procedure' field
        const procedures = Array.isArray(res.procedure) ? res.procedure : (res.procedure ? [res.procedure] : []);
        
        // Handle payment data - ensure all payment fields exist
        const payments = res.payments || {
          doctorPayment: null,
          nursingPayment: null,
          pharmacyPayment: null,
          laboratoryPayment: null,
          ambulancePayment: null,
          physiotherapyPayment: null,
          otherPayment: null,
        };

        // Load custom payments
        this.customPayments = res.customPayments || [];
        
        this.manualRecordForm.patchValue({
          ...res,
          procedures: procedures,
          payments: payments,
          grandTotal: res.grandTotal || 0,
          date: FormattedDate,
        });
      },
      error: (err) => {
        console.error("Error fetching procedures:", err);
        this.alerts
          .open("Failed to load procedures.", {
            appearance: "error",
            label: "Error",
          })
          .subscribe();
      },
      complete: () => (this.isLoading = false),
    });
  }

  setupConditionalValidation(): void {
    this.manualRecordForm
      .get("clientName")
      ?.valueChanges.subscribe((clientName) => {
        const clientAddress = this.manualRecordForm.get("clientAddress");
        const clientContact = this.manualRecordForm.get("clientContact");

        if (clientName) {
          clientContact?.setValidators([Validators.required]);
        } else {
          clientAddress?.clearValidators();
          clientContact?.clearValidators();
        }

        clientAddress?.updateValueAndValidity();
        clientContact?.updateValueAndValidity();
      });
  }

  setupPaymentCalculation(): void {
    this.manualRecordForm.get('payments')?.valueChanges.subscribe(() => {
      this.calculateGrandTotal();
    });
  }
  

  calculateGrandTotal(): void {
    const payments = this.manualRecordForm.get('payments')?.value || {};
    
    // Calculate standard payments
    const standardTotal = Object.values(payments).reduce((sum: number, value: any) => {
      // normalize to string, remove thousand separators, parse float
      const num = parseFloat(String(value).replace(/,/g, '')) || 0;
      return sum + num;
    }, 0);
    
    // Calculate custom payments
    const customTotal = this.customPayments.reduce((sum: number, payment) => {
      const num = parseFloat(String(payment.value).replace(/,/g, '')) || 0;
      return sum + num;
    }, 0);
    
    const grandTotal = standardTotal + customTotal;
  
    // update without retriggering valueChanges
    this.manualRecordForm.patchValue({ grandTotal }, { emitEvent: false });
  }

  addCustomPayment(): void {
    if (this.newPaymentName.trim() && this.newPaymentValue !== null && this.newPaymentValue > 0) {
      // Check if payment name already exists
      const existingPayment = this.customPayments.find(p => p.name.toLowerCase() === this.newPaymentName.trim().toLowerCase());
      
      if (existingPayment) {
        this.alerts
          .open("This payment type already exists.", {
            appearance: "warning",
            label: "Warning",
          })
          .subscribe();
        return;
      }
      
      this.customPayments.push({
        name: this.newPaymentName.trim(),
        value: this.newPaymentValue
      });
      
      this.newPaymentName = '';
      this.newPaymentValue = null;
      
      // Recalculate grand total
      this.calculateGrandTotal();
      
      this.alerts
        .open("Custom payment added successfully.", {
          appearance: "success",
          label: "Success",
        })
        .subscribe();
    } else {
      this.alerts
        .open("Please enter both payment name and value.", {
          appearance: "warning",
          label: "Warning",
        })
        .subscribe();
    }
  }

  removeCustomPayment(index: number): void {
    this.customPayments.splice(index, 1);
    
    // Recalculate grand total
    this.calculateGrandTotal();
    
    this.alerts
      .open("Custom payment removed successfully.", {
        appearance: "success",
        label: "Success",
      })
      .subscribe();
  }

  updateCustomPaymentValue(index: number, value: number | null): void {
    this.customPayments[index].value = value;
    this.calculateGrandTotal();
  }

  onCustomPaymentInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value ? parseFloat(target.value) : null;
    this.updateCustomPaymentValue(index, value);
  }

  editCustomPayment(index: number): void {
    const payment = this.customPayments[index];
    const newValue = prompt(`Edit ${payment.name} amount:`, payment.value?.toString() || '0');
    if (newValue !== null) {
      const value = parseFloat(newValue) || 0;
      this.updateCustomPaymentValue(index, value);
    }
  }
  

  addNewProcedure(): void {
    if (this.newProcedure.trim() && !this.allProcedures.includes(this.newProcedure.trim())) {
      const procedureToAdd = this.newProcedure.trim();
      this.allProcedures.push(procedureToAdd);
      
      // Add the new procedure to the current selection
      const currentProcedures = this.manualRecordForm.get('procedures')?.value || [];
      this.manualRecordForm.patchValue({
        procedures: [...currentProcedures, procedureToAdd]
      });
      
      this.newProcedure = '';
      this.showAddNewProcedure = false; // Hide the add new procedure section
      this.selectedProcedure = ''; // Reset dropdown selection
      
      this.alerts
        .open("New procedure added successfully.", {
          appearance: "success",
          label: "Success",
        })
        .subscribe();
    } else if (this.newProcedure.trim() && this.allProcedures.includes(this.newProcedure.trim())) {
      this.alerts
        .open("This procedure already exists.", {
          appearance: "warning",
          label: "Warning",
        })
        .subscribe();
    }
  }

  removeProcedure(procedure: string): void {
    const currentProcedures = this.manualRecordForm.get('procedures')?.value || [];
    const updatedProcedures = currentProcedures.filter((p: string) => p !== procedure);
    this.manualRecordForm.patchValue({
      procedures: updatedProcedures
    });
  }

  onProcedureChange(event: any, procedure: string): void {
    const currentProcedures = this.manualRecordForm.get('procedures')?.value || [];
    
    if (event.target.checked) {
      // Add procedure if not already selected
      if (!currentProcedures.includes(procedure)) {
        this.manualRecordForm.patchValue({
          procedures: [...currentProcedures, procedure]
        });
      }
    } else {
      // Remove procedure
      const updatedProcedures = currentProcedures.filter((p: string) => p !== procedure);
      this.manualRecordForm.patchValue({
        procedures: updatedProcedures
      });
    }
  }

  onProcedureDropdownChange(selectedValue: string): void {
    this.selectedProcedure = selectedValue;
    
    if (selectedValue === 'Other') {
      this.showAddNewProcedure = true;
    } else if (selectedValue && selectedValue !== 'Other') {
      this.showAddNewProcedure = false;
      // Add the selected procedure to the form
      const currentProcedures = this.manualRecordForm.get('procedures')?.value || [];
      if (!currentProcedures.includes(selectedValue)) {
        this.manualRecordForm.patchValue({
          procedures: [...currentProcedures, selectedValue]
        });
      }
      // Reset dropdown selection
      this.selectedProcedure = '';
    }
  }

  isProcedureSelected(procedure: string): boolean {
    const currentProcedures = this.manualRecordForm.get('procedures')?.value || [];
    return currentProcedures.includes(procedure);
  }

  stringifyProcedure = (item: string): string => item;

  submitForm(): void {
    if (this.manualRecordForm.invalid) {
      this.manualRecordForm.markAllAsTouched();
      return;
    }

    const formData = this.manualRecordForm.value;
    
    // Calculate grand total before sending
    this.calculateGrandTotal();
    
    // Send procedures as array to match backend model
    // Remove chargeRefNo as it's generated by the backend
    const { chargeRefNo, ...formDataWithoutRef } = formData;
    const payload = {
      ...formDataWithoutRef,
      procedure: formData.procedures || [],
      customPayments: this.customPayments.filter(payment => payment.value !== null && payment.value > 0),
      grandTotal: formData.grandTotal || 0
    };

    if (this.isEditMode) {
      this.manualRecordService
        .updateManualRecord(this.recordId, payload)
        .subscribe({
          next: (res) => {
            this.alerts
              .open("Record updated successfully.", {
                appearance: "success",
                label: "Success",
              })
              .subscribe();
            this.router.navigate(["/admin/manual-records"]);
          },
          error: (err) => {
            console.error("Error updating record:", err);
            this.alerts
              .open("Failed to update record.", {
                appearance: "error",
                label: "Error",
              })
              .subscribe();
          },
          complete: () => (this.isLoading = false),
        });
    } else {
      this.manualRecordService.createManualRecord(payload).subscribe({
        next: (res) => {
          this.alerts
            .open("Record created successfully.", {
              appearance: "success",
              label: "Success",
            })
            .subscribe();
          this.router.navigate(["/admin/manual-records"]);
        },
        error: (err) => {
          console.error("Error creating record:", err);
          this.alerts
            .open("Failed to create record.", {
              appearance: "error",
              label: "Error",
            })
            .subscribe();
        },
        complete: () => (this.isLoading = false),
      });
    }
  }
}
